import { useEffect, useState } from 'react'
import { api } from '@carrent/api'
import { Icon } from '@carrent/ui'
import { CatalogSettings } from './CatalogSettings'

/** Настройки панели: имя, интервал обновления, кто её видит. */

const INTERVALS = [
  { value: 0, label: 'Вручную' },
  { value: 15, label: '15 секунд' },
  { value: 60, label: '1 минута' },
  { value: 300, label: '5 минут' },
  { value: 900, label: '15 минут' },
]

const SCOPES = [
  { value: 'private', label: 'Только я' },
  { value: 'role', label: 'Моя роль' },
  { value: 'region', label: 'Регион' },
  { value: 'company', label: 'Вся компания' },
]

export function DashboardSettingsModal({
  board,
  catalogItems,
  canManageCatalog,
  onSaveBoard,
  onSaveCatalog,
  onDelete,
  onClose,
}) {
  const [name, setName] = useState(board.name)
  const [interval, setInterval] = useState(board.refresh_interval_sec)
  const [scope, setScope] = useState(board.share.scope)
  const [role, setRole] = useState(board.share.role || 'manager')
  const [regionId, setRegionId] = useState(board.share.region_id || null)
  const [regions, setRegions] = useState([])
  const [regionsError, setRegionsError] = useState(false)
  const [catalog, setCatalog] = useState(catalogItems)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Регионы тянем только когда до них дошло дело: панель открывают
  // ради интервала и названия куда чаще, чем ради области доступа.
  useEffect(() => {
    if (scope !== 'region' || regions.length) return
    let cancelled = false
    api
      .get('/regions')
      .then(({ data }) => {
        if (cancelled) return
        const items = data.data || []
        setRegions(items)
        setRegionsError(false)
        setRegionId((cur) => cur ?? items[0]?.id ?? null)
      })
      .catch(() => !cancelled && setRegionsError(true))
    return () => {
      cancelled = true
    }
  }, [scope, regions.length])

  // Регион без выбранного значения превратил бы панель в невидимую
  // для всех, кроме владельца — молча этого не делаем.
  const blocked = scope === 'region' && !regionId

  const save = () => {
    if (blocked) return
    onSaveBoard({
      name,
      refresh_interval_sec: interval,
      share: {
        scope,
        role: scope === 'role' ? role : null,
        region_id: scope === 'region' ? regionId : null,
      },
    })
    if (canManageCatalog) {
      onSaveCatalog(
        catalog.map((i) => ({
          type: i.type,
          enabled: i.enabled,
          allowed_roles: i.allowed_roles,
        })),
      )
    }
    onClose()
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Настройки панели">
      <div className="modal modal--wide">
        <header className="modal__head">
          <h2 className="modal__title">Настройки панели</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="modal__body">
          <label className="field">
            <span className="field__label">Название</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </label>

          <label className="field">
            <span className="field__label">Обновлять данные</span>
            <select
              className="input"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
            >
              {INTERVALS.map((i) => (
                <option key={i.value} value={i.value}>
                  {i.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Кто видит панель</span>
            <select className="input" value={scope} onChange={(e) => setScope(e.target.value)}>
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          {scope === 'region' && (
            <label className="field">
              <span className="field__label">Регион</span>
              {regionsError ? (
                <span className="field__hint field__hint--warn">
                  Список регионов не загрузился. Выберите другую область доступа
                  или повторите позже.
                </span>
              ) : (
                <select
                  className="input"
                  value={regionId ?? ''}
                  disabled={!regions.length}
                  onChange={(e) => setRegionId(Number(e.target.value))}
                >
                  {regions.length === 0 && <option value="">Загрузка…</option>}
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              )}
            </label>
          )}

          {scope === 'role' && (
            <label className="field">
              <span className="field__label">Роль</span>
              <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="manager">Менеджеры</option>
                <option value="admin">Администраторы</option>
              </select>
            </label>
          )}

          {canManageCatalog && (
            <CatalogSettings items={catalog} onChange={setCatalog} />
          )}
        </div>

        <footer className="modal__foot modal__foot--split">
          {confirmDelete ? (
            <span className="modal__confirm">
              Удалить панель без возврата?
              <button type="button" className="btn btn--danger btn--sm" onClick={onDelete}>
                Удалить
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setConfirmDelete(false)}
              >
                Отмена
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirmDelete(true)}
            >
              Удалить панель
            </button>
          )}

          <span className="modal__foot-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Отмена
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={save}
              disabled={blocked}
            >
              Сохранить
            </button>
          </span>
        </footer>
      </div>
    </div>
  )
}
